import { useMemo } from 'react'

import dayjs from 'dayjs'
import * as _ from 'lodash-es'

import { calc_APY_between } from '@repo/common/calculations/apy_math'

/**
 * @param list sorted from oldest to newest
 */
export function find_nav_of_date<
  Item extends Record<string, unknown>,
  DateKey extends keyof Item & string,
>({
  list,
  for_date,
  same_unit = 'day',
  date_key,
}: {
  list: Item[]
  for_date: dayjs.Dayjs
  same_unit?: dayjs.OpUnitType
  date_key: DateKey
}) {
  const typed_list = list as (Item & { [date_key]: string | number })[]

  const result = _.find(typed_list, ({ [date_key]: date }, index) => {
    const same = dayjs(date).isSame(for_date, same_unit)
    if (same) return true

    const next = typed_list[index + 1]?.[date_key]
    if (next && dayjs(next).isAfter(for_date, 'day')) return true
    return false
  })
  if (result != null) return result
  return _.maxBy(list, date_key) ?? null
}

export function to_apy<
  Item extends Record<string, unknown>,
  ItemKey extends keyof Item & string,
>({
  first,
  second,
  date_key,
  value_key,
}: {
  first: Item | undefined
  second: Item | undefined
  date_key: ItemKey
  value_key: ItemKey
}): number | null {
  if (first == null || second == null) return null
  return calc_APY_between(
    {
      balance: first[value_key] as number,
      date: first[date_key] as string | number,
    },
    {
      balance: second[value_key] as number,
      date: second[date_key] as string | number,
    },
  )
}

export function to_rate<
  Item extends Record<string, unknown>,
  ItemKey extends keyof Item & string,
>({
  first,
  second,
  value_key,
}: {
  first: Item | undefined
  second: Item | undefined
  value_key: ItemKey
}): number | null {
  if (first == null || second == null) return null
  return _.round(
    (100 * (Number(second[value_key]) - Number(first[value_key]))) /
      Number(first[value_key]),
    2,
  )
}

export const find_one_month = (today: dayjs.Dayjs) => today.subtract(1, 'month')
export const find_one_quarter = (today: dayjs.Dayjs) =>
  today.subtract(3, 'month')
export const find_jan_1 = (today: dayjs.Dayjs) => today.startOf('year')
export const find_one_year = (today: dayjs.Dayjs) => today.subtract(1, 'year')

export type CommonReturns = {
  since_date: string
  rate: number | null
  apy: number | null
}

export function calc_nav_returns<
  Item extends Record<string, unknown>,
  ItemKey extends keyof Item & string,
>({
  past_nav,
  latest_nav,
  date_key,
  value_key,
}: {
  past_nav: Item | null
  latest_nav: Item | null
  date_key: ItemKey
  value_key: ItemKey
}): CommonReturns {
  if (past_nav == null || latest_nav == null) {
    return {
      since_date: dayjs().format('YYYY-MM-DD'),
      rate: null,
      apy: null,
    }
  }

  return {
    since_date: dayjs(past_nav[date_key] as string).format('YYYY-MM-DD'),
    rate: to_rate({ first: past_nav, second: latest_nav, value_key }),
    apy: to_apy({
      first: past_nav,
      second: latest_nav,
      date_key,
      value_key,
    }),
  }
}

export function calc_nav_returns_from_list<
  Item extends Record<string, unknown>,
  ItemKey extends keyof Item & string,
>({
  list,
  from_date_maker,
  date_key,
  value_key,
}: {
  list: Item[]
  from_date_maker: 'inception' | ((today: dayjs.Dayjs) => dayjs.Dayjs)
  date_key: ItemKey
  value_key: ItemKey
}): CommonReturns {
  list = _.sortBy(list, date_key)
  const latest_nav = _.maxBy(list, date_key) ?? null

  const past_date =
    from_date_maker == 'inception'
      ? dayjs(_.minBy(list, date_key)?.[date_key] as string)
      : from_date_maker(dayjs(new Date()))

  const past_nav = find_nav_of_date({
    list,
    for_date: past_date,
    date_key,
  })

  return calc_nav_returns({ past_nav, latest_nav, date_key, value_key })
}

export function useCalcReturns({
  list,
  from_date_maker,
  date_key,
  value_key,
}: Parameters<typeof calc_nav_returns_from_list>[0]) {
  return useMemo(
    () =>
      calc_nav_returns_from_list({
        list,
        from_date_maker,
        date_key,
        value_key,
      }),
    [list, from_date_maker, date_key, value_key],
  )
}

export function calc_smooth_values_by_month<
  Item extends Record<string, unknown>,
  ItemKey extends keyof Item & string,
>({
  list,
  date_key = 'date' as ItemKey,
  value_key = 'price_per_share' as ItemKey,
}: {
  list: Item[]
  date_key?: ItemKey
  value_key?: ItemKey
}): number[] {
  if (list.length <= 1) {
    return _.map(list, value_key) as number[]
  }

  const sorted = _.sortBy(list, date_key)
  const first_item = sorted[0]!
  const latest_item = _.last(sorted)!

  const months_count =
    dayjs(latest_item[date_key] as string).diff(
      first_item[date_key] as string,
      'month',
    ) + 1

  const valuations: number[] = []

  _.times(months_count, (month_index) => {
    const for_date = dayjs(first_item[date_key] as string).add(
      month_index,
      'month',
    )

    const current_nav = find_nav_of_date({
      list: sorted,
      for_date,
      date_key,
      same_unit: 'month',
    })
    if (current_nav != null) {
      valuations.push(current_nav[value_key] as number)
    }
  })

  return valuations
}
